// src/postcards/storage.js
// Postcard collection persistence, extracted from App.jsx — Phase B Step 2
// (Jun 2026). Sibling to src/stamps/storage.js.
//
// All reads/writes go through safeStorage (the SSR/Safari-ITP/quota-safe
// wrapper) instead of raw localStorage, and failures are reported through
// jgaErrorReport instead of being silently swallowed (the four `catch {}`
// blocks the original had).
//
// NOTE: assumes safeStorage exposes a localStorage-shaped getItem/setItem.
// If lib/safeStorage.js uses different method names, this is the only place
// to adjust — see the calls below.

import { jgaErrorReport, safeStorage } from "../lib/safeStorage";

export const POSTCARD_STORAGE_KEY = "jga-postcards";

// Read current postcards from storage. Returns a flat object keyed by
// postcard ID, same shape as getStamps(). Never throws.
export function getPostcards() {
  try {
    const raw = safeStorage.getItem(POSTCARD_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch (err) {
    jgaErrorReport(err, "postcards.storage.getPostcards", { key: POSTCARD_STORAGE_KEY });
    return {};
  }
}

// Persist a postcard's state. State must be "dreaming" or "visited".
// Preserves extended metadata on state change. Mirrors setStampState.
export function setPostcardState(postcardId, state) {
  if (state !== "dreaming" && state !== "visited") return getPostcards();
  const postcards = getPostcards();
  const prev = postcards[postcardId] || {};
  postcards[postcardId] = {
    state,
    createdAt: prev.createdAt || Date.now(),
    updatedAt: Date.now(),
    visitedAt: prev.visitedAt || null,
    visitedNote: prev.visitedNote || null,
    plannedFor: prev.plannedFor || null,
  };
  if (state === "visited" && !postcards[postcardId].visitedAt) {
    postcards[postcardId].visitedAt = Date.now();
  }
  try {
    safeStorage.setItem(POSTCARD_STORAGE_KEY, JSON.stringify(postcards));
  } catch (err) {
    jgaErrorReport(err, "postcards.storage.setPostcardState", { postcardId, state });
  }
  return postcards;
}

// Partial update of a postcard's extended metadata (visitedAt, visitedNote,
// plannedFor). No-op if the postcard doesn't exist yet.
export function updatePostcardMeta(postcardId, patch) {
  const postcards = getPostcards();
  if (!postcards[postcardId]) return postcards;
  postcards[postcardId] = {
    ...postcards[postcardId],
    ...patch,
    updatedAt: Date.now(),
  };
  try {
    safeStorage.setItem(POSTCARD_STORAGE_KEY, JSON.stringify(postcards));
  } catch (err) {
    jgaErrorReport(err, "postcards.storage.updatePostcardMeta", { postcardId });
  }
  return postcards;
}

// Remove a postcard from explicit collection. After clearing, the postcard
// may still appear in the user's view if its destination is still being
// discovered via boards/wishlist/trips (derivation).
export function clearPostcard(postcardId) {
  const postcards = getPostcards();
  delete postcards[postcardId];
  try {
    safeStorage.setItem(POSTCARD_STORAGE_KEY, JSON.stringify(postcards));
  } catch (err) {
    jgaErrorReport(err, "postcards.storage.clearPostcard", { postcardId });
  }
  return postcards;
}

// Defensive default export from the start — mirrors safeStorage.js's
// multi-named-export + default shape, which keeps CRA's Webpack happy and
// avoids a likely deploy debug cycle.
const postcardStorage = {
  POSTCARD_STORAGE_KEY,
  getPostcards,
  setPostcardState,
  updatePostcardMeta,
  clearPostcard,
};
export default postcardStorage;
